import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "@/axiosConfig";
import {
  IActivity,
  ICase,
  ICaseDetails,
  IDocument,
  IPageination,
  IStatuses,
  ITableFilter,
  IServiceResponce,
  ICategories,
  ICasePrivacy,
  IDocumentPrivacy,
  ICasePayload,
  ICaseEditsState,
} from "./store.types";
import { ICategoriesedFilterMenu } from "@adl/smart-ui-library";
import isEqual from "lodash/isEqual";

export const NAME = "cases";

export interface IState {
  cases: IServiceResponce<ICase>;
  profile: IServiceResponce<ICaseDetails>;
  status: IStatuses | null;
  activites: IServiceResponce<IActivity>;
  savingCaseUpdates: IServiceResponce<string>;
  selectedDocument: IDocument | null;
  selectedCase: ICase | null;
  selectedCaseSummary: string | null;
  selectedCasePrivicy: number | null;
  categories: ICategories[] | null;
  casePrivicyLevels: ICasePrivacy[] | null;
  documentPrivacyLevels: IDocumentPrivacy[] | null;
  caseEditsToSave: IDocument | ICaseEditsState | null;
  industries: ICategoriesedFilterMenu[] | null;
  methodologies: ICategoriesedFilterMenu[] | null;
  uploadComplete: boolean;
  additionalMappings: any;
}

const initialState: IState = {
  cases: {
    data: null,
    pagination: { skip: 0, top: 10, total: 0 },
    isLoading: false,
    hasError: false,
  },
  profile: {
    data: null,
    pagination: { skip: 0, top: 10, total: 0 },
    isLoading: false,
    hasError: false,
  },
  activites: {
    data: null,
    pagination: null,
    isLoading: false,
    hasError: false,
  },
  savingCaseUpdates: {
    data: null,
    pagination: null,
    isLoading: false,
    hasError: false,
  },
  selectedDocument: null,
  selectedCase: null,
  status: null,
  selectedCaseSummary: null,
  selectedCasePrivicy: null,
  categories: null,
  casePrivicyLevels: null,
  caseEditsToSave: null,
  documentPrivacyLevels: null,
  uploadComplete: false,
  industries: null,
  methodologies: null,
  additionalMappings: null,
};

export type CasesPayload = {
  include: string;
  sort: string;
  top: number;
  skip: number;
  filter?: { items: ITableFilter[] };
};

export const fetchAllCases = createAsyncThunk(
  `${NAME}/fetchAllCases`,
  async (args: CasesPayload, thunkAPI) => {
    try {
      const response = await axiosInstance.post(`/cases`, args, {
        signal: thunkAPI.signal,
      });
      return response.data;
    } catch (error: any) {
      if (error.name === "CanceledError") {
        return thunkAPI.rejectWithValue({ canceled: true });
      }
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCaseDetailsById = createAsyncThunk(
  `${NAME}/fetchCaseDetailsById`,
  async (
    arg: {
      deliveryEngagementId: string | number;
      top: number;
      skip: number;
      hideLoading?: boolean;
    },
    thunkAPI
  ) => {
    try {
      const response = await axiosInstance.get(
        `/cases/details/${arg.deliveryEngagementId}?top=${arg.top}&skip=${arg.skip}`
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchCaseActivitiesById = createAsyncThunk(
  `${NAME}/fetchCaseActivitiesById`,
  async (arg: { deliveryEngagementId: string | number }, thunkAPI) => {
    try {
      const response = await axiosInstance.get(
        `/cases/activities/${arg.deliveryEngagementId}`
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const deleteDocumentbyId = createAsyncThunk(
  `${NAME}/deleteDocumentbyId`,
  async (
    arg: { deliveryEngagementId: string | number; documentId: number | string },
    thunkAPI
  ) => {
    try {
      const response = await axiosInstance.delete(
        `/cases/details/${arg.deliveryEngagementId}/${arg.documentId}`
      );
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getDocumentCategories = createAsyncThunk(
  `${NAME}/getDocumentCategories`,
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`/documents/categories`);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getCasePrivicyLevels = createAsyncThunk(
  `${NAME}/getCasePrivicyLevels`,
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`/cases/privacy-levels`);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getIndustries = createAsyncThunk(
  `${NAME}/getIndustries`,
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`/cases/industries`);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getMethodologies = createAsyncThunk(
  `${NAME}/getMethodologies`,
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`/cases/methodologies`);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const getDocumentPrivicyLevels = createAsyncThunk(
  `${NAME}/getDocumentPrivicyLevels`,
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`/documents/privacy-types`);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export interface IDocumentEditsPayload {
  deliveryEngagementId: string | number;
  payload: IDocument;
}

export const putDocumentDetails = createAsyncThunk(
  `${NAME}/putCaseDetails`,
  async (arg: IDocumentEditsPayload, thunkAPI) => {
    const { deliveryEngagementId, payload } = arg;

    const formatedPayload: ICasePayload = {
      documents: [
        {
          documentId: payload.documentId,
          summary: payload.summary,
          fileNameOriginal: payload.fileNameOriginal,
          categoryId: payload.categoryId,
          documentPrivacyLevels: payload.documentPrivacyLevels,
        },
      ],
    };

    try {
      const response = await axiosInstance.put(
        `/cases/details/${deliveryEngagementId}`,
        formatedPayload
      );
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export interface ICaseEditsPayload {
  deliveryEngagementId: string | number;
  payload: ICaseEditsState;
}

export const putCaseDetails = createAsyncThunk(
  `${NAME}/putCaseDetails`,
  async (arg: ICaseEditsPayload, thunkAPI) => {
    const { deliveryEngagementId, payload } = arg;

    const { summary } = payload;
    const formatedPayload: any = {
      caseDetails: {},
    };
    if (summary != null) {
      formatedPayload.caseDetails!["summary"] = summary;
    }

    try {
      const response = await axiosInstance.put(
        `/cases/details/${deliveryEngagementId}`,
        formatedPayload
      );
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

const reducers = {
  setSelectedCase: (state: IState, { payload: data }) => {
    state.selectedCase = data as ICase;
  },
  setSelectedDocument: (state: IState, { payload: data }) => {
    state.selectedDocument = data as IDocument;
  },

  setSelectedCaseStatuses: (state: IState, { payload: data }) => {
    state.status = data as IStatuses;
  },
  setSelectedCasePagination: (state: IState, { payload: data }) => {
    state.profile.pagination = data as IPageination;
  },
  setUploadComplete: (state: IState, { payload: data }) => {
    state.uploadComplete = data as boolean;
  },
  setCaseEditsToSave: (state: IState, { payload: data }) => {
    state.caseEditsToSave = data as IDocument | ICaseEditsState;
    state.savingCaseUpdates.data = null;
  },
  setCasePrivacyLevel: (state: IState, { payload: data }) => {
    state.selectedCasePrivicy = data as number;
  },
  setCaseSummary: (state: IState, { payload: data }) => {
    state.selectedCaseSummary = data as string;
  },
  setAdditionalMappings: (state: IState, { payload: data }) => {
    state.additionalMappings = data as any;
  },
  setSelectedCaseActivites: (state: IState, { payload: data }) => {
    state.activites.data = data as IActivity[];
  },
};

export const casesSlice = createSlice({
  name: NAME,
  initialState,
  reducers,
  extraReducers: (builder) => {
    builder.addCase(fetchAllCases.pending, (state) => {
      const clone = { ...state.cases };
      clone.isLoading = true;
      clone.data = clone.data ? [] : null;
      clone.hasError = false;
      state.cases = clone;
      state.selectedCase = null;
      state.selectedDocument = null;
      state.status = null;
      state.selectedCaseSummary = null;
      state.selectedCasePrivicy = null;
      state.activites = { ...initialState.activites };
    });
    builder.addCase(fetchAllCases.fulfilled, (state, action) => {
      const clone = { ...state.cases };
      clone.data = action.payload.results as ICase[];
      clone.pagination = action.payload.pagination as IPageination;
      clone.isLoading = false;
      state.cases = clone;
    });
    builder.addCase(fetchAllCases.rejected, (state, action) => {
      if (action.meta.aborted) {
        return;
      }
      const clone = { ...state.cases };
      clone.isLoading = false;
      clone.hasError = true;
      state.cases = clone;
    });
    builder.addCase(fetchCaseDetailsById.pending, (state, action) => {
      const { meta } = action;
      const clone = { ...state.profile };
      clone.isLoading = meta.arg.hideLoading ? false : true;
      state.profile = clone;
      state.selectedDocument = null;

      state.selectedCaseSummary = null;
      state.selectedCasePrivicy = null;
    });
    builder.addCase(fetchCaseDetailsById.fulfilled, (state, action) => {
      const newData = [...action.payload.results].reverse() as ICaseDetails[];
      const newPagination = action.payload.pagination as IPageination;
      const newStatuses = action.payload.statuses as IStatuses;
      const newSummary = action.payload.summary;
      const newPrivacy = action.payload.casePrivacyLevelId;
      const newAdditionalMappings = {
        ...action.payload.additionalMappings,
        markets: action.payload.businessUnit,
        currency: action.payload.currency,
      };

      const currentProfile = state.profile;

      const hasDataChanged =
        !isEqual(currentProfile.data, newData) ||
        !isEqual(currentProfile.pagination, newPagination) ||
        !isEqual(state.status, newStatuses) ||
        !isEqual(state.selectedCaseSummary, newSummary) ||
        state.selectedCasePrivicy !== newPrivacy ||
        !isEqual(state.additionalMappings, newAdditionalMappings);

      if (!hasDataChanged) {
        return;
      }

      state.profile = {
        ...currentProfile,
        data: newData,
        pagination: newPagination,
        isLoading: false,
      };
      state.status = newStatuses;
      state.selectedCaseSummary = newSummary;
      state.selectedCasePrivicy = newPrivacy;
      state.additionalMappings = newAdditionalMappings;
    });
    builder.addCase(fetchCaseDetailsById.rejected, (state) => {
      const clone = { ...state.profile };
      clone.isLoading = false;
      clone.hasError = true;
      state.profile = clone;
    });
    builder.addCase(fetchCaseActivitiesById.pending, (state) => {
      const clone = { ...state.activites };
      clone.isLoading = true;
      clone.hasError = false;
      state.activites = clone;
    });
    builder.addCase(fetchCaseActivitiesById.fulfilled, (state, action) => {
      const clone = { ...state.activites };
      clone.data = action.payload.results as IActivity[];
      clone.isLoading = false;
      state.activites = clone;
    });
    builder.addCase(fetchCaseActivitiesById.rejected, (state) => {
      const clone = { ...state.activites };
      clone.isLoading = false;
      clone.hasError = true;
      state.activites = clone;
    });
    builder.addCase(getDocumentCategories.fulfilled, (state, action) => {
      state.categories = action.payload.data.results as ICategories[];
    });
    builder.addCase(getDocumentPrivicyLevels.fulfilled, (state, action) => {
      state.documentPrivacyLevels = action.payload.data
        .results as IDocumentPrivacy[];
    });
    builder.addCase(getIndustries.fulfilled, (state, action) => {
      state.industries = action.payload.data
        .results as ICategoriesedFilterMenu[];
    });
    builder.addCase(getMethodologies.fulfilled, (state, action) => {
      state.methodologies = action.payload.data
        .results as ICategoriesedFilterMenu[];
    });
    builder.addCase(getCasePrivicyLevels.fulfilled, (state, action) => {
      state.casePrivicyLevels = action.payload.data.results as ICasePrivacy[];
    });
    builder.addCase(putDocumentDetails.pending, (state) => {
      const clone = { ...state.savingCaseUpdates };
      clone.isLoading = true;
      clone.hasError = false;
      state.savingCaseUpdates = clone;
    });
    builder.addCase(putDocumentDetails.fulfilled, (state) => {
      const clone = { ...state.savingCaseUpdates };
      clone.isLoading = false;
      clone.data = null;
      clone.hasError = false;
      state.savingCaseUpdates = clone;
      state.caseEditsToSave = null;
    });
    builder.addCase(putDocumentDetails.rejected, (state) => {
      const clone = { ...state.savingCaseUpdates };
      clone.isLoading = false;
      clone.data = null;
      clone.hasError = true;
      state.savingCaseUpdates = clone;
    });
  },
});
/*
documentPrivacyLevels
*/
export const selectCasesState = (state) => state[NAME].cases;
export const selectIsLoading = (state) => state[NAME].cases.isLoading;
export const selectIsError = (state) => state[NAME].cases.hasError;
export const selectCases = (state) => state[NAME].cases.data;
export const selectPagination = (state) => state[NAME].cases.pagination;
/*

*/
export const selectProfileState = (state) => state[NAME].profile;
export const selectSelectedCaseDetails = (state) => state[NAME].profile.data;
export const selectIsProfileLoading = (state) => state[NAME].profile.isLoading;
export const selectProfilePagination = (state) =>
  state[NAME].profile.pagination;
export const selectIsProfileError = (state) => state[NAME].profile.hasError;
/*

*/
export const selectActivitesState = (state) => state[NAME].activites;
export const selectActivitesIsLoading = (state) =>
  state[NAME].activites.isLoading;
export const selectActivitesIsError = (state) => state[NAME].activites.hasError;
export const selectActivites = (state) => state[NAME].activites.data;
export const selectActivitesPagination = (state) =>
  state[NAME].activites.pagination;

/*

*/
export const selectSelectedCase = (state) => state[NAME].selectedCase;
export const selectSelectedDocument = (state) => state[NAME].selectedDocument;
export const selectSelectedCaseStatus = (state) => state[NAME].status;
export const selectDocumentCatagories = (state) => state[NAME].categories;
export const selectCasePrivacyLevels = (state) => state[NAME].casePrivicyLevels;
export const selectDocumentPrivacyLevels = (state) =>
  state[NAME].documentPrivacyLevels;
export const selectUploadComplete = (state) => state[NAME].uploadComplete;
export const selectDeliveryEngagementId = (state) => {
  return state[NAME].selectedCase?.DeliveryEngagementId;
};

export const selectSelectedCaseSummary = (state) =>
  state[NAME].selectedCaseSummary;
export const selectSelectedCasePrivicyLevel = (state) =>
  state[NAME].selectedCasePrivicy;
export const selectEditsToSave = (state) => state[NAME].caseEditsToSave;

export const selectIndustries = (state) => state[NAME].industries;

export const selectMethodologies = (state) => state[NAME].methodologies;
export const selectAdditionalMappings = (state) =>
  state[NAME].additionalMappings;

export const selectIsSaving = (state) =>
  state[NAME].savingCaseUpdates.isLoading;
export const selectSavedSuccess = (state) =>
  Boolean(state[NAME].savingCaseUpdates.data);
/*

*/
export const {
  setSelectedCase,
  setSelectedDocument,
  setSelectedCasePagination,
  setSelectedCaseStatuses,
  setUploadComplete,
  setCaseEditsToSave,
  setCasePrivacyLevel,
  setCaseSummary,
  setAdditionalMappings,
  setSelectedCaseActivites,
} = casesSlice.actions;

export default casesSlice.reducer;
